import { useState, useEffect, useRef } from "react"
import { useStudioActions } from "../../hooks/useStudioActions"
import {
  FiCpu, FiZap, FiLayout, FiMaximize2, FiActivity, FiTerminal,
  FiCode, FiBox, FiCheckCircle, FiLoader, FiAlertTriangle, FiPlus,
  FiFileText, FiShare2, FiDownload, FiChevronRight, FiShield, FiTrendingUp,
  FiInfo, FiSettings, FiLayers, FiCompass, FiUploadCloud, FiX
} from "react-icons/fi"
import { useNotification } from "../../context/NotificationContext"
// @ts-ignore
import mermaid from "mermaid"
// @ts-ignore
import ReactMarkdown from "react-markdown"

// Initialize Mermaid with Light Theme support
const initMermaid = () => {
  const mermaidInstance = (window as any).mermaid || mermaid;
  if (mermaidInstance) {
    mermaidInstance.initialize({
      startOnLoad: true,
      theme: "default", 
      securityLevel: "loose",
      fontFamily: "Outfit, sans-serif",
      flowchart: { useMaxWidth: true, htmlLabels: true, curve: 'basis' }
    })
  }
}

export default function ArchitectStudio() {
  const [input, setInput] = useState("")
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [blueprint, setBlueprint] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<'visual' | 'logic' | 'analysis'>('visual')
  
  const { generateArchitectBlueprint, analyzeDoc } = useStudioActions()
  const { showNotification } = useNotification()
  const mermaidRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    initMermaid();
  }, []);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return
    
    setFile(selectedFile)
    setUploading(true)
    
    const formData = new FormData()
    formData.append("file", selectedFile)

    try {
      const data = await analyzeDoc(formData)
      setInput(prev => prev ? `${prev}\n\n--- Document: ${selectedFile.name} ---\n${data.content}` : data.content)
      showNotification(`"${selectedFile.name}" synced to forge!`, "success")
    } catch (error: any) {
      showNotification("Failed to parse document.", "error")
    } finally {
      setUploading(false)
    }
  }

  const handleForge = async () => {
    if (!input.trim()) return
    setLoading(true)
    try {
      const data = await generateArchitectBlueprint(input, "")
      const parsed = parseBlueprint(data.blueprint)
      setBlueprint(parsed)
      showNotification("Architectural Blueprint Forged!", "success")
    } catch (error: any) {
      showNotification("Failed to forge blueprint.", "error")
    } finally {
      setLoading(false)
    }
  }

  const parseBlueprint = (text: string) => {
    const mermaidMatch = text.match(/```mermaid([\s\S]*?)```/)
    let mermaidChart = mermaidMatch ? mermaidMatch[1].trim() : ""
    
    // Sanitize Mermaid Chart (Remove common AI hallucinations)
    if (mermaidChart) {
      // Ensure it starts with a valid type if it's missing
      if (!mermaidChart.startsWith('graph') && !mermaidChart.startsWith('sequenceDiagram') && !mermaidChart.startsWith('classDiagram')) {
        mermaidChart = `graph TD\n${mermaidChart}`
      }
      // Fix common syntax errors like unquoted labels with parentheses
      mermaidChart = mermaidChart.split('\n').map(line => {
        if (line.includes('(') && !line.includes('"')) {
          return line.replace(/(\w+)\(([^)]+)\)/g, '$1["$1 ($2)"]')
        }
        return line
      }).join('\n')
    }

    const remainingText = text.replace(/```mermaid[\s\S]*?```/, "").trim()
    
    return {
      raw: text,
      mermaid: mermaidChart,
      markdown: remainingText
    }
  }

  useEffect(() => {
    const mermaidInstance = (window as any).mermaid || mermaid;
    if (blueprint?.mermaid && activeTab === 'visual' && mermaidRef.current && mermaidInstance) {
      try {
        mermaidRef.current.removeAttribute('data-processed')
        // Force a re-render by clearing innerHTML if needed
        mermaidRef.current.innerHTML = blueprint.mermaid
        mermaidInstance.contentLoaded()
      } catch (err) {
        console.error("Mermaid Render Error:", err)
      }
    }
  }, [blueprint, activeTab])

  return (
    <div className="min-vh-100 bg-white overflow-hidden position-relative pt-5">
      {/* Decorative Background Elements */}
      <div className="position-absolute top-0 start-0 w-100 h-100 opacity-50 pointer-events-none">
        <div className="blob animate-morph bg-primary" style={{ top: '-10%', right: '-10%', width: '800px', height: '800px', opacity: 0.1 }}></div>
        <div className="blob animate-morph bg-info" style={{ bottom: '-10%', left: '-5%', width: '600px', height: '600px', opacity: 0.08, animationDelay: '-4s' }}></div>
      </div>

      <div className="container py-5 mt-5 position-relative z-10">
        {/* Header Section */}
        <div className="text-center mb-5 animate-fade-in-up">
          <div className="badge bg-primary bg-opacity-10 rounded-pill px-4 py-2 text-primary fw-900 mb-3 tracking-widest shadow-sm border border-primary border-opacity-10">
            ENGINEERING STUDIO
          </div>
          <h2 className="display-4 fw-900 mb-2 tracking-tight text-dark">
            Architect <span className="text-gradient-premium">Intelligence</span>
          </h2>
          <p className="text-muted fw-medium fs-5 max-w-lg mx-auto">
            Upload documentation or paste code to forge high-fidelity blueprints.
          </p>
        </div>

        {!blueprint ? (
          <div className="row justify-content-center animate-fade-in-up">
            <div className="col-xl-11">
              <div className="card-premium glass p-4 p-md-5 border-0 shadow-2xl rounded-5 bg-white bg-opacity-70">
                <div className="row g-5">
                  {/* Left: Input Area */}
                  <div className="col-lg-7">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <div className="d-flex align-items-center gap-3">
                        <div className="icon-box mb-0 shadow-glow bg-primary"><FiTerminal /></div>
                        <h4 className="text-dark fw-900 mb-0">Blueprint Input</h4>
                      </div>
                      
                      <div className="d-flex gap-2">
                         <button 
                           className="btn btn-sm glass rounded-pill px-3 py-2 fw-900 text-primary border-0 shadow-sm"
                           onClick={() => document.getElementById('arch-file-upload')?.click()}
                           disabled={uploading}
                         >
                           {uploading ? <FiLoader className="spin" /> : <FiUploadCloud className="me-2" />}
                           {uploading ? "Parsing..." : "Upload Doc"}
                         </button>
                         <input id="arch-file-upload" type="file" className="d-none" onChange={handleFileUpload} />
                         {input && (
                            <button className="btn btn-sm text-danger rounded-circle p-2 hover-bg-light" onClick={() => {setInput(""); setFile(null)}}>
                               <FiX size={18} />
                            </button>
                         )}
                      </div>
                    </div>
                    
                    <div className="position-relative mb-4">
                      <textarea
                        className="form-control glass border-light border-opacity-50 rounded-4 p-4 text-dark custom-scrollbar fw-medium"
                        style={{ height: '350px', resize: 'none', background: 'white', fontSize: '1.05rem', boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.02)' }}
                        placeholder="Paste requirements here or upload a documentation file (PDF/TXT)..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                      />
                      {file && !uploading && (
                         <div className="position-absolute top-0 end-0 m-3 animate-fade-in">
                            <div className="badge bg-success bg-opacity-10 text-success rounded-pill px-3 py-2 border border-success border-opacity-20 x-small fw-bold d-flex align-items-center gap-2">
                               <FiCheckCircle /> {file.name}
                            </div>
                         </div>
                      )}
                    </div>

                    <button 
                      className="btn-premium w-100 py-4 fs-4 rounded-pill shadow-glow border-0 transition-all hover-scale"
                      onClick={handleForge}
                      disabled={loading || uploading || !input.trim()}
                    >
                      {loading ? (
                        <><FiLoader className="spin me-2" /> SYNTHESIZING...</>
                      ) : (
                        <><FiZap className="me-2" /> FORGE BLUEPRINT</>
                      )}
                    </button>
                  </div>

                  {/* Right: Helpful Info */}
                  <div className="col-lg-5">
                    <div className="bg-light bg-opacity-50 rounded-5 p-4 h-100 border border-light">
                      <h5 className="fw-900 text-dark mb-4 d-flex align-items-center gap-2">
                        <FiInfo className="text-primary" /> Engineering Guide
                      </h5>
                      <div className="d-flex flex-column gap-4">
                        {[
                          { icon: <FiLayout />, title: "Visual Synthesis", desc: "Generates high-fidelity Mermaid.js diagrams from your docs." },
                          { icon: <FiTrendingUp />, title: "Logic Optimizer", desc: "Refactors complex system flows into optimized patterns." },
                          { icon: <FiShield />, title: "Risk Audit", desc: "Identifies security and performance bottlenecks." },
                          { icon: <FiCompass />, title: "Tech Mapping", desc: "Suggests the best-fit modern stack for your needs." }
                        ].map((item, i) => (
                          <div key={i} className="d-flex gap-3">
                            <div className="text-primary bg-white p-2 rounded-3 shadow-sm h-fit">{item.icon}</div>
                            <div>
                              <div className="fw-900 text-dark small">{item.title}</div>
                              <div className="x-small text-muted fw-medium">{item.desc}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="mt-5 p-4 rounded-4 bg-primary bg-opacity-5 border border-primary border-opacity-10">
                        <div className="x-small fw-900 text-primary uppercase tracking-widest mb-1">Supported Formats</div>
                        <p className="small text-dark fw-medium mb-0 opacity-70">
                          Upload PDFs, Text files, or Code documents for complete system analysis.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="row g-4 animate-scale-in">
            {/* Dashboard Navigation */}
            <div className="col-lg-12">
              <div className="glass p-2 rounded-pill shadow-sm border border-light d-flex gap-2 bg-white bg-opacity-80">
                {[
                  { id: 'visual', icon: <FiLayout />, label: 'Visual Blueprint' },
                  { id: 'logic', icon: <FiZap />, label: 'Logic Optimizer' },
                  { id: 'analysis', icon: <FiTrendingUp />, label: 'Strategic Analysis' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`btn flex-grow-1 d-flex align-items-center justify-content-center gap-3 py-3 rounded-pill fw-900 transition-all border-0 ${activeTab === tab.id ? 'btn-primary shadow-glow text-white' : 'text-muted hover-bg-light'}`}
                  >
                    {tab.icon} {tab.label}
                  </button>
                ))}
                <button className="btn btn-outline-danger border-0 rounded-pill px-4 fw-900" onClick={() => {setBlueprint(null); setFile(null); setInput("")}}>
                  New Forge
                </button>
              </div>
            </div>

            {/* Dashboard Content */}
            <div className="col-lg-12">
              <div className="card-premium glass border-0 shadow-2xl rounded-5 overflow-hidden bg-white" style={{ minHeight: '600px' }}>
                <div className="p-4 p-md-5 h-100 custom-scrollbar overflow-auto">
                  {activeTab === 'visual' && (
                    <div className="animate-fade-in-up printable-document">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="text-dark fw-900 mb-0 no-print">System Flow Diagram</h4>
                        <button className="btn btn-sm glass rounded-pill px-3 border-0 shadow-sm no-print" onClick={() => window.print()}>
                          <FiDownload size={16} className="text-primary me-2" /> Export Blueprint
                        </button>
                      </div>
                      
                      {blueprint.mermaid ? (
                        <div className="mermaid-wrapper rounded-5 p-5 bg-light bg-opacity-30 border border-light overflow-auto d-flex justify-content-center shadow-inner">
                          <div ref={mermaidRef} className="mermaid">
                            {blueprint.mermaid}
                          </div>
                        </div>
                      ) : (
                        <div className="py-5 text-center opacity-40">
                          <FiAlertTriangle size={60} className="mb-4 text-warning" />
                          <h4 className="text-dark fw-900">Complex Topology Detected</h4>
                          <p className="text-muted">Diagram generation was skipped to prioritize textual clarity.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'logic' && (
                    <div className="animate-fade-in-up printable-document">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="text-dark fw-900 mb-0 no-print">Shrunken Optimization Logic</h4>
                        <button className="btn btn-sm glass rounded-pill px-3 border-0 shadow-sm no-print" onClick={() => window.print()}>
                          <FiDownload size={16} className="text-primary me-2" /> Export Blueprint
                        </button>
                      </div>
                      <div className="markdown-content text-dark opacity-80">
                         {ReactMarkdown ? (
                           <ReactMarkdown>{blueprint.markdown}</ReactMarkdown>
                         ) : (
                           <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{blueprint.markdown}</pre>
                         )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'analysis' && (
                    <div className="animate-fade-in-up printable-document">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="text-dark fw-900 mb-0 no-print">Strategic Analysis</h4>
                        <button className="btn btn-sm glass rounded-pill px-3 border-0 shadow-sm no-print" onClick={() => window.print()}>
                          <FiDownload size={16} className="text-primary me-2" /> Export Blueprint
                        </button>
                      </div>
                      
                      <div className="row g-4 mb-5 no-print">
                        {[
                          { title: "Scalability", value: "High", color: "success", icon: <FiMaximize2 /> },
                          { title: "Optimization", value: "Shrunken", color: "primary", icon: <FiZap /> },
                          { title: "Stability", value: "Stable", color: "info", icon: <FiShield /> }
                        ].map((stat, i) => (
                          <div key={i} className="col-md-4">
                            <div className="bg-light bg-opacity-50 p-4 rounded-4 border border-light text-center shadow-sm">
                              <div className={`text-${stat.color} mb-2`}>{stat.icon}</div>
                              <div className="x-small text-muted fw-900 uppercase tracking-widest mb-1">{stat.title}</div>
                              <div className={`fs-3 fw-900 text-${stat.color}`}>{stat.value}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="bg-light bg-opacity-30 p-4 p-md-5 rounded-5 border border-light">
                        <h4 className="text-dark fw-900 mb-4">Core Recommendations</h4>
                        <div className="markdown-content text-dark opacity-80">
                           {ReactMarkdown ? (
                             <ReactMarkdown>{blueprint.markdown.split('Bottleneck Analysis')[1] || "Select 'Logic Optimizer' for full details."}</ReactMarkdown>
                           ) : (
                             <pre style={{ whiteSpace: 'pre-wrap' }}>{blueprint.markdown.split('Bottleneck Analysis')[1] || "Full analysis generated in optimizer tab."}</pre>
                           )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .bg-white { background-color: #ffffff !important; }
        .glass {
          background: rgba(255, 255, 255, 0.7) !important;
          backdrop-filter: blur(15px) !important;
          -webkit-backdrop-filter: blur(15px) !important;
          border: 1px solid rgba(255, 255, 255, 0.5) !important;
        }
        
        .markdown-content h1, .markdown-content h2, .markdown-content h3 {
          color: #0f172a;
          font-weight: 900;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }

        .markdown-content p {
          line-height: 1.8;
          font-size: 1.1rem;
          color: #334155;
          margin-bottom: 1.5rem;
        }

        .markdown-content code {
          background: #f1f5f9;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          color: var(--primary);
          font-weight: 700;
        }

        .markdown-content ul {
          padding-left: 1.2rem;
          margin-bottom: 2rem;
        }

        .markdown-content li {
          margin-bottom: 0.8rem;
          font-weight: 500;
        }

        .mermaid-wrapper {
          background: white !important;
          box-shadow: inset 0 2px 15px rgba(0,0,0,0.05);
        }

        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @media (max-width: 991px) {
          .display-4 { font-size: 2.5rem; }
        }
      `}</style>
    </div>
  )
}
